import React, { useEffect, useState, useCallback } from 'react';
import AdminLayout from '../components/AdminLayout';
import api from '../lib/api';
import { RefreshCw, RefreshCcw, Download } from 'lucide-react';
import { toast } from 'sonner';

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

function fmtMoney(v) {
  if (v == null) return '—';
  return Number(v).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function StatusPill({ status }) {
  const tone =
    status === 'filled' ? 'text-emerald-400 border-emerald-500/40 bg-emerald-500/5'
    : status === 'partially_filled' ? 'text-emerald-300 border-emerald-500/30'
    : status === 'submitted' ? 'text-amber-400 border-amber-500/40'
    : status === 'expired' ? 'text-amber-300 border-amber-500/30'
    : status === 'rejected' || status === 'failed' ? 'text-red-400 border-red-500/40 bg-red-500/5'
    : 'text-zinc-400 border-white/10';
  return <span className={`text-[10px] font-mono px-2 py-1 border rounded-sm ${tone}`}>{(status || '—').toUpperCase()}</span>;
}

function PnLCell({ pnl, plpc }) {
  if (pnl == null) return <span className="text-zinc-600">—</span>;
  const pos = pnl >= 0;
  return (
    <span className={`font-mono tabular-nums ${pos ? 'text-emerald-400' : 'text-red-400'}`}>
      {pos ? '+' : ''}{fmtMoney(pnl)}
      {plpc != null && <span className="text-[10px] opacity-70 ml-1">({pos ? '+' : ''}{(plpc * 100).toFixed(2)}%)</span>}
    </span>
  );
}

export default function AdminTrades() {
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reconciling, setReconciling] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/trades');
      setTrades(data.items || []);
    } catch (e) {
      console.error('[admin/trades] load failed', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleReconcile = async () => {
    setReconciling(true);
    try {
      const { data } = await api.post('/admin/reconcile');
      toast.success(`Reconciled ${data.users_processed} user${data.users_processed === 1 ? '' : 's'}.`);
      await load();
    } catch (e) {
      toast.error('Reconcile failed.');
    } finally {
      setReconciling(false);
    }
  };

  const exportUrl = `${api.defaults.baseURL}/admin/trades/export`;

  return (
    <AdminLayout title="Trades" description="Paper trade ledger. Every execution that hit Alpaca, with live fill / position / P&L data after reconcile.">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <p className="text-sm text-zinc-400 font-mono">{loading ? 'Loading…' : `${trades.length} trades`}</p>
        <div className="flex items-center gap-2">
          <button
            onClick={handleReconcile}
            disabled={reconciling}
            data-testid="admin-reconcile-btn"
            className="inline-flex items-center gap-2 text-sm px-4 py-2 border gold-border gold-text hover:bg-[#d8b34a] hover:text-black rounded-sm transition-all disabled:opacity-60"
          >
            <RefreshCcw size={13} className={reconciling ? 'animate-spin' : ''} />
            {reconciling ? 'Reconciling…' : 'Reconcile from Alpaca'}
          </button>
          <a
            href={exportUrl}
            data-testid="admin-trades-export"
            className="inline-flex items-center gap-2 text-sm px-4 py-2 border border-white/10 text-zinc-300 hover:border-[#d8b34a]/50 hover:text-white rounded-sm transition-colors"
          >
            <Download size={13} /> Export CSV
          </a>
          <button onClick={load} data-testid="admin-trades-refresh" className="inline-flex items-center gap-2 text-sm px-4 py-2 border border-white/10 text-zinc-300 hover:border-[#d8b34a]/50 hover:text-white rounded-sm transition-colors">
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      <div className="border border-white/10 rounded-md overflow-hidden bg-[#0c0c0e]">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.03] border-b border-white/10">
              <tr className="text-left text-[10px] tracking-[0.3em] font-mono text-zinc-500">
                <th className="px-4 py-3">USER</th>
                <th className="px-4 py-3">SYMBOL</th>
                <th className="px-4 py-3">SIDE</th>
                <th className="px-4 py-3">STATUS</th>
                <th className="px-4 py-3">QTY / FILLED</th>
                <th className="px-4 py-3">LIMIT / FILL</th>
                <th className="px-4 py-3">NOTIONAL</th>
                <th className="px-4 py-3">UNREALIZED P&L</th>
                <th className="px-4 py-3">ORDER ID</th>
                <th className="px-4 py-3">CREATED</th>
              </tr>
            </thead>
            <tbody data-testid="admin-trades-tbody">
              {trades.map(t => (
                <tr key={t.execution_id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`admin-trade-${t.execution_id}`}>
                  <td className="px-4 py-3 text-zinc-100">{t.user_email}</td>
                  <td className="px-4 py-3 font-mono text-white">{t.symbol}</td>
                  <td className="px-4 py-3 font-mono">
                    <span className={t.side === 'buy' ? 'text-emerald-400' : 'text-red-400'}>{t.side?.toUpperCase()}</span>
                  </td>
                  <td className="px-4 py-3"><StatusPill status={t.status} /></td>
                  <td className="px-4 py-3 tabular-nums text-zinc-300">{t.qty ?? '—'}{t.filled_qty != null && ` / ${t.filled_qty}`}</td>
                  <td className="px-4 py-3 tabular-nums text-zinc-300">{t.limit_price ? `$${t.limit_price}` : '—'}{t.fill_price != null && ` / $${(+t.fill_price).toFixed(2)}`}</td>
                  <td className="px-4 py-3 tabular-nums text-zinc-300">{t.target_notional != null ? fmtMoney(t.target_notional) : '—'}</td>
                  <td className="px-4 py-3"><PnLCell pnl={t.unrealized_pnl} plpc={t.unrealized_plpc} /></td>
                  <td className="px-4 py-3 text-zinc-500 font-mono text-xs">{t.alpaca_order_id ? `${t.alpaca_order_id.slice(0, 10)}…` : '—'}</td>
                  <td className="px-4 py-3 text-xs text-zinc-500 font-mono whitespace-nowrap">{fmtDate(t.created_at)}</td>
                </tr>
              ))}
              {!loading && trades.length === 0 && (
                <tr><td colSpan={10} className="px-4 py-10 text-center text-sm text-zinc-500">
                  No paper trades yet. Run <strong className="text-zinc-300">Reconcile from Alpaca</strong> after the first auto-trade.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}
